import type { Config, State, WidgetOptions } from './types'
import { MessageSourceRegistry } from './MessageSourceRegistry'
import { OptimizedMessageListener } from './OptimizedMessageListener'
import { ButtonMessageHandler } from './handlers/ButtonMessageHandler'
import { ChatMessageHandler } from './handlers/ChatMessageHandler'
import { GreetingMessageHandler } from './handlers/GreetingMessageHandler'
import styles from './style.css?inline'
import {getParentColorScheme as resolveParentColorScheme} from './parentColorScheme'
import {getParentIsMobileViewport} from './parentViewport'

const STORAGE_KEY = 'alloy-webchat'
const GREETING_DELAY_MS = 5000
const GREETING_ENABLED = false
const CHAT_OPEN_CLASS = 'alloy-webchat-widget-open'
const CHAT_LOADING_CLASS = 'alloy-webchat-widget-loading'
const CHAT_WIDGET_MIN_WIDTH = 450
const CHAT_WIDGET_MIN_HEIGHT = 700
const CHAT_WIDGET_MIN_USABLE_HEIGHT = 220
const SMALL_VIEWPORT_WARNING_DURATION_MS = 10000
const SCROLL_LOCK_CLASS = 'alloy-webchat-widget-scroll-locked'
const LAUNCHER_SIDE_STORAGE_KEY = 'launcher_side'
const BUTTON_DROP_TARGET_HIT_RADIUS = 36
const MOBILE_BACK_GUARD_STATE_KEY = '__alloyWebchatMobileBackGuard'

type LauncherSide = 'left' | 'right'

export class WidgetLoader {
  private config: Config = {
    apiUrl: null,
    assetUrl: null,
    iconAssetUrl: null,
    wsUrl: null,
    apiKey: null,
    eventId: null,
    contact: null,
    isOpen: false,
    showWidgetCloseButton: true,
    positioning: 'fixed',
    container: null,
  }

  private state: State = {
    isInitialized: false,
    isInitializing: false,
    isOpen: false,
    chatIframe: null,
    buttonIframe: null,
    greetingIframe: null,
    widgetContainer: null,
    greetingTimeout: null,
    greetingDismissed: false,
    settings: null,
  }

  private pendingOpen = false
  private settingsOverrides: Record<string, unknown> = {}

  private unreadCount = 0
  private isVoiceCallActive = false
  private launcherSide: LauncherSide = 'right'
  private autoHiddenForSmallViewport = false
  private smallViewportWarningElement: HTMLDivElement | null = null
  private smallViewportWarningTimer: ReturnType<typeof setTimeout> | null = null
  private buttonDragOverlay: HTMLDivElement | null = null
  private buttonDropTarget: HTMLDivElement | null = null
  private buttonSideDragState:
    | {
        startPageX: number
        lastPageX: number
        startSide: LauncherSide
        targetSide: LauncherSide
        isDropTargetActive: boolean
      }
    | null = null

  private sourceRegistry: MessageSourceRegistry | null = null
  private messageListener: OptimizedMessageListener | null = null
  private parentPointerDownListener: ((event: PointerEvent) => void) | null = null
  private systemThemeListener: ((event: MediaQueryListEvent) => void) | null = null
  private siteThemeObserver: MutationObserver | null = null
  private resizeClampListener: (() => void) | null = null
  private mobileBackGuardActive = false
  private resizeState:
    | {
        direction: string
        startX: number
        startY: number
        startWidth: number
        startHeight: number
        startSide: LauncherSide
        startRight: number
        startBottom: number
        startLeft: number
        startTop: number
        containerWidth: number
        containerHeight: number
        minLeft: number
        minTop: number
        minRight: number
        minBottom: number
        maxWidth: number
        maxHeight: number
        minWidth: number
        minHeight: number
      }
    | null = null

  public async init(options: WidgetOptions): Promise<void> {
    if (this.state.isInitialized || this.state.isInitializing || this.state.widgetContainer) return

    const envUrls = this.resolveEnvUrlsFromScript()
    const resolvedApiUrl = options.apiUrl || envUrls.apiUrl || import.meta.env.VITE_API_URL || ''

    this.config.eventId = options.eventId || null
    this.config.apiKey = options.apiKey || null
    this.config.apiUrl = this.resolveBrowserApiUrl(resolvedApiUrl)
    this.config.assetUrl = this.resolveAssetUrl(resolvedApiUrl)
    this.config.iconAssetUrl = options.iconAssetUrl?.trim().replace(/\/+$/, '') || null
    this.config.wsUrl = options.wsUrl || envUrls.wsUrl || import.meta.env.VITE_WS_URL || ''
    this.config.isOpen = options.isOpen || false
    this.config.showWidgetCloseButton = options.showWidgetCloseButton ?? true
    this.config.contact = options.contact || null
    this.config.language = this.state.language = options.language || (navigator.language.split('-')[0] === 'ru' ? 'ru' : 'en')
    this.config.positioning = options.positioning || 'fixed'
    this.config.container = options.container || null

    if (!this.config.apiKey) {
      this.logError('apiKey is required')
      this.showError('Chat widget configuration error: apiKey is required')
      this.state.isInitializing = false
      return
    }

    if (!this.config.apiUrl || !this.config.wsUrl) {
      this.logError('apiUrl and wsUrl are required')
      this.showError('Chat widget configuration error: apiUrl and wsUrl are required')
      this.state.isInitializing = false
      return
    }

    if (!this.config.eventId) {
      this.logError('eventId is required')
      this.showError('Chat widget configuration error: eventId is required')
      this.state.isInitializing = false
      return
    }

    try {
      this.pendingOpen = this.config.isOpen
      this.createWidgetUI()
      this.setupSystemThemeListener()
      this.setupSiteThemeObserver()
    } catch (error) {
      this.logError('Initialization error:', error)
      this.showError('Failed to initialize chat widget. Please check your configuration.')
      this.state.isInitializing = false
    }
  }

  public open(): void {
    if (this.state.isOpen) {
      return
    }

    if (!this.state.settings) {
      this.pendingOpen = true
      return
    }

    this.pendingOpen = false
    if (!this.canDisplayChat()) {
      this.handleSmallViewportUnavailable({showWarning: true})
      return
    }

    this.autoHiddenForSmallViewport = false
    this.hideSmallViewportWarning()
    this.toggleChat()
  }

  public close(): void {
    this.pendingOpen = false
    if (this.state.isOpen) {
      this.toggleChat()
    }
  }

  public sendMessage(content: string): void {
    this.sendMessageToChatIframe('sendMessage', { content, isRetry: false })
  }

  public applySettings(settings: Record<string, unknown>): void {
    this.settingsOverrides = {...this.settingsOverrides, ...settings}
    if (this.state.settings) this.publishSettings()
  }

  private handleSettingsLoaded(settings: Record<string, unknown>): void {
    this.state.settings = settings
    this.publishSettings()
    if (this.pendingOpen) this.open()
  }

  private publishSettings(): void {
    const settings = {...this.state.settings, ...this.settingsOverrides}
    this.state.settings = settings
    const parentColorScheme = this.getParentColorScheme()
    const parentIsMobile = getParentIsMobileViewport()
    this.applyChatLoadingTheme()
    this.sendMessageToButtonIframe('applySettings', { settings, parentColorScheme })
    this.sendMessageToChatIframe('applySettings', { settings, parentColorScheme, parentIsMobile })
    this.sendMessageToGreetingIframe('applySettings', { settings, parentColorScheme })
  }

  public updateStyle(variables: Record<string, string>): void {
    this.sendMessageToButtonIframe('updateStyle', { variables })
    this.sendMessageToChatIframe('updateStyle', { variables })
  }

  public destroy(): void {
    if (this.messageListener) {
      this.messageListener.detach()
      this.messageListener = null
    }

    if (this.parentPointerDownListener) {
      document.removeEventListener('pointerdown', this.parentPointerDownListener, true)
      this.parentPointerDownListener = null
    }

    if (this.sourceRegistry) {
      this.sourceRegistry.clear()
      this.sourceRegistry = null
    }

    if (this.systemThemeListener) {
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this.systemThemeListener)
      this.systemThemeListener = null
    }

    if (this.siteThemeObserver) {
      this.siteThemeObserver.disconnect()
      this.siteThemeObserver = null
    }
    if (this.resizeClampListener) {
      window.removeEventListener('resize', this.resizeClampListener)
      this.resizeClampListener = null
    }
    this.disableMobileBackGuard()
    this.cleanupButtonSideDrag()

    if (this.state.greetingTimeout) {
      clearTimeout(this.state.greetingTimeout)
      this.state.greetingTimeout = null
    }

    if (this.smallViewportWarningTimer) {
      clearTimeout(this.smallViewportWarningTimer)
      this.smallViewportWarningTimer = null
    }

    this.smallViewportWarningElement = null

    if (this.state.widgetContainer && this.state.widgetContainer.parentNode) {
      this.state.widgetContainer.parentNode.removeChild(this.state.widgetContainer)
    }

    this.setParentScrollLock(false)

    this.pendingOpen = false
    this.settingsOverrides = {}
    this.state = {
      isInitialized: false,
      isInitializing: false,
      isOpen: false,
      chatIframe: null,
      buttonIframe: null,
      greetingIframe: null,
      widgetContainer: null,
      greetingTimeout: null,
      greetingDismissed: false,
      settings: null,
    }
  }

  private createWidgetUI(): void {
    const styleSheet = document.createElement('style')
    styleSheet.textContent = styles
    document.head.appendChild(styleSheet)

    this.launcherSide = this.getStoredLauncherSide()
    this.state.widgetContainer = document.createElement('div')
    this.state.widgetContainer.className = 'alloy-webchat-widget-container'
    
    const positioning = this.config.positioning || 'fixed'
    if (positioning === 'absolute') {
      this.state.widgetContainer.style.position = 'absolute'
      this.state.widgetContainer.style.width = '100%'
      this.state.widgetContainer.style.height = '100%'
    } else {
      this.state.widgetContainer.style.position = 'fixed'
    }

    this.state.buttonIframe = document.createElement('iframe')
    this.state.buttonIframe.className = 'alloy-webchat-widget-button-iframe'
    this.state.buttonIframe.style.opacity = '0';
    this.state.buttonIframe.style.pointerEvents = 'none';
    this.state.buttonIframe.setAttribute('allow', 'autoplay *')
    this.state.buttonIframe.setAttribute('title', 'Chat Button')

    this.smallViewportWarningElement = document.createElement('div')
    this.smallViewportWarningElement.className = 'alloy-webchat-widget-small-viewport-warning'
    this.smallViewportWarningElement.setAttribute('role', 'status')
    this.smallViewportWarningElement.setAttribute('aria-live', 'polite')
    this.smallViewportWarningElement.textContent = this.getSmallViewportWarningMessage()

    if (GREETING_ENABLED) {
      this.state.greetingIframe = document.createElement('iframe')
      this.state.greetingIframe.className = 'alloy-webchat-widget-greeting-iframe'
      this.state.greetingIframe.style.opacity = '0';
      this.state.greetingIframe.style.pointerEvents = 'none';
      this.state.greetingIframe.setAttribute('allow', 'autoplay *')
      this.state.greetingIframe.setAttribute('title', 'Chat Greeting')
    }

    const iframeContainer = document.createElement('div')
    iframeContainer.className = 'alloy-webchat-widget-iframe-container'
    iframeContainer.id = 'alloy-webchat-widget-iframe-container'
    const loadingShell = document.createElement('div')
    loadingShell.className = 'alloy-webchat-widget-loading-shell'
    loadingShell.setAttribute('role', 'status')
    loadingShell.setAttribute('aria-label', 'Loading chat')
    loadingShell.innerHTML = '<div class="alloy-webchat-widget-loading-spinner" aria-hidden="true"></div>'
    iframeContainer.appendChild(loadingShell)
    this.attachResizeHandles(iframeContainer)

    if (this.state.greetingIframe) {
      this.state.widgetContainer.appendChild(this.state.greetingIframe)
    }
    this.state.widgetContainer.appendChild(this.state.buttonIframe)
    this.state.widgetContainer.appendChild(this.smallViewportWarningElement)
    this.state.widgetContainer.appendChild(iframeContainer)

    let targetContainer: HTMLElement | null = null
    if (this.config.container) {
      if (typeof this.config.container === 'string') {
        targetContainer = document.querySelector(this.config.container) as HTMLElement
      } else {
        targetContainer = this.config.container
      }
    }
    
    if (targetContainer) {
      targetContainer.appendChild(this.state.widgetContainer)
      const containerStyle = window.getComputedStyle(targetContainer)
      if (containerStyle.position === 'static') {
        targetContainer.style.position = 'relative'
      }
    } else {
      document.body.appendChild(this.state.widgetContainer)
    }

    requestAnimationFrame(() => {
      if (this.state.buttonIframe) {
        this.state.buttonIframe.src = `${this.getAssetUrl()}/widget/src/button/index.html`
      }
      if (this.state.greetingIframe) {
        this.state.greetingIframe.src = `${this.getAssetUrl()}/widget/src/greeting/index.html`
      }
    })

    this.initializeMessageSystem()
    this.registerIframeSources()
    this.applyLauncherSide(this.launcherSide, {persist: false, animate: false})
    this.setupResizeClampListener()
    this.setupParentPointerDownListener()

  }

  private getWidgetScriptSrc(): string | null {
    const currentScript = document.currentScript as HTMLScriptElement | null
    if (currentScript?.src) return currentScript.src

    const scripts = Array.from(document.scripts) as HTMLScriptElement[]
    const widgetScript = scripts.find((script) => {
      const src = script.src
      return src && /alloy-webchat-widget(\.min)?\.js(\?.*)?$/.test(src)
    })

    return widgetScript?.src ?? null
  }

  private resolveEnvUrlsFromScript(): { apiUrl: string | null; wsUrl: string | null } {
    const currentScriptSrc = this.getWidgetScriptSrc()
    if (!currentScriptSrc) return { apiUrl: null, wsUrl: null }

    try {
      const scriptHost = new URL(currentScriptSrc).host
      const apiProxyUrl = import.meta.env.VITE_API_PROXY_URL
      const apiUrl = import.meta.env.VITE_API_URL
      const wsProxyUrl = import.meta.env.VITE_WS_PROXY_URL
      const wsUrl = import.meta.env.VITE_WS_URL

      if (apiProxyUrl && new URL(apiProxyUrl).host === scriptHost) {
        return { apiUrl: apiProxyUrl || null, wsUrl: wsProxyUrl || null }
      }

      if (apiUrl && new URL(apiUrl).host === scriptHost) {
        return { apiUrl: apiUrl || null, wsUrl: wsUrl || null }
      }

      return { apiUrl: null, wsUrl: null }
    } catch {
      return { apiUrl: null, wsUrl: null }
    }
  }

  private resolveAssetUrl(apiUrl: string | null): string | null {
    if (!apiUrl) return null

    try {
      const apiProxyUrl = import.meta.env.VITE_API_PROXY_URL
      const viteAssetUrl = import.meta.env.VITE_API_URL
      const apiProxyHost = apiProxyUrl ? new URL(apiProxyUrl).hostname : ''
      const viteAssetHost = viteAssetUrl ? new URL(viteAssetUrl).hostname : ''

      if (
        import.meta.env.DEV &&
        apiProxyUrl &&
        viteAssetUrl &&
        this.isLoopbackHost(apiProxyHost) &&
        this.isLoopbackHost(viteAssetHost) &&
        new URL(apiUrl).host === new URL(apiProxyUrl).host &&
        new URL(viteAssetUrl).host !== new URL(apiProxyUrl).host
      ) {
        return viteAssetUrl.replace(/\/+$/, '')
      }
    } catch {
      return apiUrl
    }

    return apiUrl.replace(/\/+$/, '')
  }

  private resolveBrowserApiUrl(apiUrl: string | null): string | null {
    if (!apiUrl) return null

    try {
      const apiProxyUrl = import.meta.env.VITE_API_PROXY_URL
      const viteApiUrl = import.meta.env.VITE_API_URL
      const apiProxyHost = apiProxyUrl ? new URL(apiProxyUrl).hostname : ''
      const viteApiHost = viteApiUrl ? new URL(viteApiUrl).hostname : ''

      if (
        import.meta.env.DEV &&
        apiProxyUrl &&
        viteApiUrl &&
        this.isLoopbackHost(apiProxyHost) &&
        this.isLoopbackHost(viteApiHost) &&
        new URL(apiUrl).host === new URL(apiProxyUrl).host &&
        new URL(viteApiUrl).host !== new URL(apiProxyUrl).host
      ) {
        return viteApiUrl.replace(/\/+$/, '')
      }
    } catch {
      return apiUrl
    }

    return apiUrl.replace(/\/+$/, '')
  }

  private isLoopbackHost(hostname: string): boolean {
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]' || hostname === '::1'
  }

  private getAssetUrl(): string {
    return this.config.assetUrl || this.config.apiUrl || ''
  }

  private initializeMessageSystem(): void {
    try {
      const expectedOrigin = this.getAssetUrl() ? new URL(this.getAssetUrl()).origin : null

      this.sourceRegistry = new MessageSourceRegistry(expectedOrigin)

      const { buttonHandler, chatHandler, greetingHandler } = this.createMessageHandlers()

      this.messageListener = new OptimizedMessageListener(
        this.sourceRegistry,
        buttonHandler,
        chatHandler,
        greetingHandler
      )

      this.messageListener.attach()
    } catch (error) {
      this.logError('Error initializing message system:', error)
    }
  }

  private createMessageHandlers() {
    const buttonHandler = new ButtonMessageHandler(
      this.config,
      this.state,
      this.sendMessageToButtonIframe.bind(this),
      this.sendMessageToChatIframe.bind(this),
      this.handleButtonToggle.bind(this),
      this.dismissGreeting.bind(this),
      this.scheduleGreeting.bind(this),
      this.logError.bind(this),
      this.showError.bind(this),
      this.handleSettingsLoaded.bind(this),
      this.handleButtonSideDragStart.bind(this),
      this.handleButtonSideDragMoveMessage.bind(this),
      this.handleButtonSideDragEndMessage.bind(this)
    )

    const chatHandler = new ChatMessageHandler(
      this.config,
      this.state,
      this.sendMessageToButtonIframe.bind(this),
      this.sendMessageToChatIframe.bind(this),
      this.toggleChat.bind(this),
      this.dismissGreeting.bind(this),
      this.scheduleGreeting.bind(this),
      this.logError.bind(this),
      this.showError.bind(this),
      (count: number) => { this.unreadCount = count },
      (isActive: boolean) => { this.isVoiceCallActive = isActive }
    )

    const greetingHandler = new GreetingMessageHandler(
      this.config,
      this.state,
      this.sendMessageToButtonIframe.bind(this),
      this.sendMessageToChatIframe.bind(this),
      this.handleButtonToggle.bind(this),
      this.dismissGreeting.bind(this),
      this.scheduleGreeting.bind(this),
      this.logError.bind(this),
      this.showError.bind(this)
    )

    return { buttonHandler, chatHandler, greetingHandler }
  }

  private registerIframeSources(): void {
    if (!this.sourceRegistry) {
      this.logError('Source registry not initialized')
      return
    }

    if (this.state.buttonIframe?.contentWindow) {
      this.sourceRegistry.registerButtonSource(this.state.buttonIframe.contentWindow)
    }

    if (this.state.chatIframe?.contentWindow) {
      this.sourceRegistry.registerChatSource(this.state.chatIframe.contentWindow)
    }

    if (this.state.greetingIframe?.contentWindow) {
      this.sourceRegistry.registerGreetingSource(this.state.greetingIframe.contentWindow)
    }

    const checkButtonIframeInterval = setInterval(() => {
      if (this.sourceRegistry && this.state.buttonIframe?.contentWindow) {
        const registered = this.sourceRegistry.registerButtonSource(this.state.buttonIframe.contentWindow)
        if (registered) {
          clearInterval(checkButtonIframeInterval)
        }
      }
    }, 100)

    setTimeout(() => {
      clearInterval(checkButtonIframeInterval)
    }, 10000)
  }

  private setupParentPointerDownListener(): void {
    if (this.parentPointerDownListener) return

    this.parentPointerDownListener = (event: PointerEvent) => {
      if (!this.state.isOpen || !this.state.chatIframe?.contentWindow) return

      const target = event.target
      if (target instanceof Node && this.state.widgetContainer?.contains(target)) {
        return
      }

      this.sendMessageToChatIframe('parentPointerDown', {})
    }

    document.addEventListener('pointerdown', this.parentPointerDownListener, true)
  }

  private sendMessageToChatIframe(type: string, payload: Record<string, unknown>): void {
    if (this.state.chatIframe && this.state.chatIframe.contentWindow) {
      const message = { type, payload }
      this.state.chatIframe.contentWindow.postMessage(message, '*')
    }
  }

  private sendMessageToButtonIframe(type: string, payload: Record<string, unknown>): void {
    if (this.state.buttonIframe && this.state.buttonIframe.contentWindow) {
      this.state.buttonIframe.contentWindow.postMessage({ type, payload }, '*')
    }
  }

  private sendMessageToGreetingIframe(type: string, payload: Record<string, unknown>): void {
    if (this.state.greetingIframe && this.state.greetingIframe.contentWindow) {
      this.state.greetingIframe.contentWindow.postMessage({ type, payload }, '*')
    }
  }

  private handleButtonToggle(): void {
    if (this.state.isOpen) {
      this.close()
      return
    }

    this.open()
  }

  private toggleChat(): void {
    if (!this.state.isOpen && !this.canDisplayChat()) {
      this.handleSmallViewportUnavailable({showWarning: true})
      return
    }

    if (!this.state.isOpen && !this.state.chatIframe) {
      this.createChatIframeIfNeeded()
    }

    this.state.isOpen = !this.state.isOpen
    const iframeContainer = document.getElementById('alloy-webchat-widget-iframe-container')

    if (iframeContainer) {
      if (this.state.isOpen) {
        this.hideGreeting()
        this.openChatContainer(iframeContainer)
        this.updateParentScrollLock()
        if (this.state.chatIframe && this.state.chatIframe.contentWindow) {
          try {
            this.state.chatIframe.focus()
          } catch {
            // Ignore focus errors
          }
        }
        if (this.resizeClampListener) {
          requestAnimationFrame(() => {
            this.resizeClampListener?.()
          })
        }
      } else {
        this.closeChatContainer(iframeContainer)
        this.setParentScrollLock(false)
      }
    }

    const parentColorScheme = this.getParentColorScheme()
    const parentIsMobile = getParentIsMobileViewport()

    this.updateMobileBackGuard(parentIsMobile)
    this.sendMessageToChatIframe(this.state.isOpen ? 'open' : 'close', { parentIsMobile })
    this.sendMessageToButtonIframe('chatStateChange', { isOpen: this.state.isOpen, parentColorScheme })
    if (this.state.isOpen) {
      this.sendMessageToButtonIframe('unreadCount', { count: 0 })
      this.sendMessageToButtonIframe('hideIndicators', {})
    } else {
      if (this.unreadCount > 0) {
        this.sendMessageToButtonIframe('unreadCount', { count: this.unreadCount })
      } else {
        this.sendMessageToButtonIframe('unreadCount', { count: 0 })
      }
      if (this.isVoiceCallActive) {
        this.sendMessageToButtonIframe('voiceCallStatus', { isActive: true })
      }
    }
  }

  private showGreeting(): void {
    if (!GREETING_ENABLED) return
    if (this.state.greetingDismissed) return
    
    const storageData = this.getStorageData()
    if (storageData.greeting_shown) {
      this.state.greetingDismissed = true
      return
    }
    if (this.state.isOpen) return
    
    this.sendMessageToGreetingIframe('showGreeting', {})
    this.setStorageData({ greeting_shown: true })
    requestAnimationFrame(() => {
      Object.assign(this.state.greetingIframe!.style, {
        opacity: '1',
        pointerEvents: 'auto'
      })
    })
  }

  private setParentScrollLock(locked: boolean): void {
    document.documentElement.classList.toggle(SCROLL_LOCK_CLASS, locked)
    document.body.classList.toggle(SCROLL_LOCK_CLASS, locked)
  }

  private updateMobileBackGuard(parentIsMobile: boolean = getParentIsMobileViewport()): void {
    if (this.state.isOpen && parentIsMobile) {
      this.enableMobileBackGuard()
      return
    }

    this.disableMobileBackGuard()
  }

  private enableMobileBackGuard(): void {
    if (this.mobileBackGuardActive) {
      return
    }

    this.mobileBackGuardActive = true
    window.addEventListener('popstate', this.handleMobileBackPopState)
    this.pushMobileBackGuardState()
  }

  private disableMobileBackGuard(): void {
    if (!this.mobileBackGuardActive) {
      return
    }

    this.mobileBackGuardActive = false
    window.removeEventListener('popstate', this.handleMobileBackPopState)
  }

  private pushMobileBackGuardState(): void {
    try {
      window.history.pushState({
        [MOBILE_BACK_GUARD_STATE_KEY]: true,
      }, '', window.location.href)
    } catch {
      // Ignore browsers or embedded hosts that reject History API writes.
    }
  }

  private handleMobileBackPopState = (): void => {
    if (!this.mobileBackGuardActive || !this.state.isOpen || !getParentIsMobileViewport()) {
      this.disableMobileBackGuard()
      return
    }

    this.pushMobileBackGuardState()
    this.sendMessageToChatIframe('mobileBrowserBack', {})
  }

  private updateParentScrollLock(): void {
    const shouldLock = this.state.isOpen
      && this.config.positioning !== 'absolute'
      && getParentIsMobileViewport()

    this.setParentScrollLock(shouldLock)
  }

  private getStoredLauncherSide(): LauncherSide {
    const storageData = this.getStorageData()
    return storageData[LAUNCHER_SIDE_STORAGE_KEY] === 'left' ? 'left' : 'right'
  }

  private applyLauncherSide(
    side: LauncherSide,
    options: {persist?: boolean; animate?: boolean} = {}
  ): void {
    const {persist = true, animate = true} = options
    const widgetContainer = this.state.widgetContainer
    if (!widgetContainer) return

    this.launcherSide = side
    widgetContainer.classList.toggle('alloy-webchat-widget-side-left', side === 'left')
    widgetContainer.classList.toggle('alloy-webchat-widget-side-right', side === 'right')

    if (animate) {
      widgetContainer.classList.add('alloy-webchat-widget-launcher-settling')
      window.setTimeout(() => {
        widgetContainer.classList.remove('alloy-webchat-widget-launcher-settling')
      }, 260)
    }

    this.applyLauncherSideToElement(this.state.buttonIframe, side, 0)
    this.applyLauncherSideToElement(this.state.greetingIframe, side, 0)

    const iframeContainer = document.getElementById('alloy-webchat-widget-iframe-container') as HTMLDivElement | null
    if (iframeContainer && !getParentIsMobileViewport()) {
      this.applyLauncherSideToElement(iframeContainer, side, 25)
      this.updateResizeHandlesForSide(iframeContainer)
      this.resizeClampListener?.()
    }

    if (persist) {
      this.setStorageData({[LAUNCHER_SIDE_STORAGE_KEY]: side})
    }
  }

  private applyLauncherSideToElement(
    element: HTMLElement | null,
    side: LauncherSide,
    edgeOffset: number
  ): void {
    if (!element) return

    element.style.transform = ''
    if (side === 'left') {
      element.style.left = `${edgeOffset}px`
      element.style.removeProperty('right')
    } else {
      element.style.right = `${edgeOffset}px`
      element.style.removeProperty('left')
    }
  }

  private getLauncherBounds(): {left: number; width: number} {
    if (this.state.widgetContainer && this.state.widgetContainer.style.position === 'absolute') {
      const rect = this.state.widgetContainer.getBoundingClientRect()
      return {left: rect.left, width: rect.width}
    }

    return {left: 0, width: window.innerWidth}
  }

  private getOppositeLauncherSide(side: LauncherSide): LauncherSide {
    return side === 'left' ? 'right' : 'left'
  }

  private handleButtonSideDragStart(payload?: Record<string, unknown>): void {
    if (this.state.isOpen) return

    const buttonIframe = this.state.buttonIframe
    const widgetContainer = this.state.widgetContainer
    if (!buttonIframe || !widgetContainer) return

    const buttonRect = buttonIframe.getBoundingClientRect()
    const startClientX = this.asNumber(payload?.startClientX) ?? buttonRect.width / 2
    const currentClientX = this.asNumber(payload?.currentClientX) ?? startClientX
    const startPageX = buttonRect.left + startClientX
    const currentPageX = buttonRect.left + currentClientX
    const targetSide = this.getOppositeLauncherSide(this.launcherSide)

    this.cleanupButtonSideDrag()
    this.buttonSideDragState = {
      startPageX,
      lastPageX: currentPageX,
      startSide: this.launcherSide,
      targetSide,
      isDropTargetActive: false
    }

    widgetContainer.classList.add('alloy-webchat-widget-launcher-dragging')
    this.ensureButtonDropTarget(targetSide)
    this.ensureButtonDragOverlay()
    window.addEventListener('pointermove', this.handleButtonSideDragMove)
    window.addEventListener('pointerup', this.handleButtonSideDragEnd)
    window.addEventListener('pointercancel', this.handleButtonSideDragEnd)
    window.addEventListener('mousemove', this.handleButtonSideMouseDragMove)
    window.addEventListener('mouseup', this.handleButtonSideDragEnd)
    window.addEventListener('blur', this.handleButtonSideDragLost)
    document.addEventListener('pointerup', this.handleButtonSideDragEnd, true)
    document.addEventListener('pointercancel', this.handleButtonSideDragEnd, true)
    document.addEventListener('mouseup', this.handleButtonSideDragEnd, true)
    this.buttonDragOverlay?.addEventListener('pointerup', this.handleButtonSideDragEnd)
    this.buttonDragOverlay?.addEventListener('pointercancel', this.handleButtonSideDragEnd)
    this.buttonDragOverlay?.addEventListener('mouseup', this.handleButtonSideDragEnd)
    this.updateButtonDragPreview(currentPageX)
  }

  private handleButtonSideDragMove = (event: PointerEvent): void => {
    if (!this.buttonSideDragState) return

    event.preventDefault()
    this.updateButtonDragPreview(event.clientX)
  }

  private handleButtonSideDragMoveMessage(payload?: Record<string, unknown>): void {
    if (!this.buttonSideDragState) return

    this.updateButtonDragPreview(this.resolveButtonDragPointerX(payload))
  }

  private handleButtonSideMouseDragMove = (event: MouseEvent): void => {
    if (!this.buttonSideDragState) return

    event.preventDefault()
    this.updateButtonDragPreview(event.clientX)
  }

  private handleButtonSideDragEnd = (event: PointerEvent | MouseEvent): void => {
    if (!this.buttonSideDragState) return

    const pointerX = Number.isFinite(event.clientX) ? event.clientX : this.buttonSideDragState.lastPageX
    this.finishButtonSideDrag(pointerX)
  }

  private handleButtonSideDragEndMessage(payload?: Record<string, unknown>): void {
    if (!this.buttonSideDragState) return

    this.finishButtonSideDrag(this.resolveButtonDragPointerX(payload))
  }

  private handleButtonSideDragLost = (): void => {
    if (!this.buttonSideDragState) return

    this.finishButtonSideDrag(this.buttonSideDragState.lastPageX)
  }

  private finishButtonSideDrag(pointerX: number): void {
    if (!this.buttonSideDragState) return

    this.updateButtonDragPreview(pointerX)
    const {isDropTargetActive, startSide, targetSide} = this.buttonSideDragState
    const nextSide: LauncherSide = isDropTargetActive ? targetSide : startSide

    this.cleanupButtonSideDrag()
    this.applyLauncherSide(nextSide, {persist: true, animate: true})
  }

  private ensureButtonDragOverlay(): void {
    if (this.buttonDragOverlay) return

    this.buttonDragOverlay = document.createElement('div')
    this.buttonDragOverlay.className = 'alloy-webchat-widget-button-drag-overlay'
    document.body.appendChild(this.buttonDragOverlay)
  }

  private ensureButtonDropTarget(side: LauncherSide): void {
    if (this.buttonDropTarget) {
      this.buttonDropTarget.remove()
    }

    this.buttonDropTarget = document.createElement('div')
    this.buttonDropTarget.className = [
      'alloy-webchat-widget-button-drop-target',
      `alloy-webchat-widget-button-drop-target-${side}`
    ].join(' ')
    this.buttonDropTarget.setAttribute('aria-hidden', 'true')
    this.buttonDropTarget.innerHTML = `
      <div class="alloy-webchat-widget-button-drop-target-surface">
        <svg class="alloy-webchat-widget-button-drop-target-icon" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="6.5" />
          <circle cx="12" cy="12" r="1.45" fill="currentColor" stroke="none" />
          <path d="M12 2.5v3" />
          <path d="M12 18.5v3" />
          <path d="M2.5 12h3" />
          <path d="M18.5 12h3" />
        </svg>
      </div>
    `
    this.positionButtonDropTarget(side)
    document.body.appendChild(this.buttonDropTarget)
    requestAnimationFrame(() => {
      this.buttonDropTarget?.classList.add('alloy-webchat-widget-button-drop-target-visible')
    })
  }

  private positionButtonDropTarget(side: LauncherSide): void {
    if (!this.buttonDropTarget) return

    const bounds = this.getLauncherBounds()
    const buttonRect = this.state.buttonIframe?.getBoundingClientRect()
    const buttonWidth = buttonRect?.width || 110
    const targetLeft = side === 'left'
      ? bounds.left
      : bounds.left + bounds.width - buttonWidth

    this.buttonDropTarget.style.left = `${targetLeft}px`
    this.buttonDropTarget.style.right = 'auto'
    if (buttonRect) {
      this.buttonDropTarget.style.top = `${buttonRect.top}px`
      this.buttonDropTarget.style.bottom = 'auto'
    }
  }

  private cleanupButtonSideDrag(): void {
    this.state.widgetContainer?.classList.remove('alloy-webchat-widget-launcher-dragging')
    this.state.buttonIframe?.style.removeProperty('transform')
    this.buttonDragOverlay?.remove()
    this.buttonDragOverlay = null
    this.buttonDropTarget?.remove()
    this.buttonDropTarget = null
    this.buttonSideDragState = null
    window.removeEventListener('pointermove', this.handleButtonSideDragMove)
    window.removeEventListener('pointerup', this.handleButtonSideDragEnd)
    window.removeEventListener('pointercancel', this.handleButtonSideDragEnd)
    window.removeEventListener('mousemove', this.handleButtonSideMouseDragMove)
    window.removeEventListener('mouseup', this.handleButtonSideDragEnd)
    window.removeEventListener('blur', this.handleButtonSideDragLost)
    document.removeEventListener('pointerup', this.handleButtonSideDragEnd, true)
    document.removeEventListener('pointercancel', this.handleButtonSideDragEnd, true)
    document.removeEventListener('mouseup', this.handleButtonSideDragEnd, true)
  }

  private updateButtonDragPreview(pointerX: number): void {
    if (!this.buttonSideDragState || !this.state.buttonIframe) return

    this.buttonSideDragState.lastPageX = pointerX
    const bounds = this.getLauncherBounds()
    const buttonWidth = this.state.buttonIframe.getBoundingClientRect().width || 110
    const naturalLeft = this.buttonSideDragState.startSide === 'left'
      ? bounds.left
      : bounds.left + bounds.width - buttonWidth
    const minDx = bounds.left - naturalLeft
    const maxDx = bounds.left + bounds.width - buttonWidth - naturalLeft
    const dx = this.clampPosition(pointerX - this.buttonSideDragState.startPageX, minDx, maxDx)

    this.state.buttonIframe.style.transform = `translateX(${dx}px) translateZ(0)`
    this.updateButtonDropTargetState(naturalLeft + dx + buttonWidth / 2)
  }

  private updateButtonDropTargetState(draggedCenterX: number): void {
    if (!this.buttonSideDragState || !this.buttonDropTarget || !this.state.buttonIframe) return

    const bounds = this.getLauncherBounds()
    const buttonWidth = this.state.buttonIframe.getBoundingClientRect().width || 110
    const targetCenterX = this.buttonSideDragState.targetSide === 'left'
      ? bounds.left + buttonWidth / 2
      : bounds.left + bounds.width - buttonWidth / 2
    const isActive = Math.abs(draggedCenterX - targetCenterX) <= BUTTON_DROP_TARGET_HIT_RADIUS

    this.buttonSideDragState.isDropTargetActive = isActive
    this.buttonDropTarget.classList.toggle('alloy-webchat-widget-button-drop-target-active', isActive)
  }

  private resolveButtonDragPointerX(payload?: Record<string, unknown>): number {
    const deltaScreenX = this.asNumber(payload?.deltaScreenX)
    if (deltaScreenX !== null && this.buttonSideDragState) {
      return this.buttonSideDragState.startPageX + deltaScreenX
    }

    const deltaClientX = this.asNumber(payload?.deltaClientX)
    if (deltaClientX !== null && this.buttonSideDragState) {
      return this.buttonSideDragState.startPageX + deltaClientX
    }

    const currentClientX = this.asNumber(payload?.currentClientX)
    if (currentClientX !== null && this.state.buttonIframe) {
      return this.state.buttonIframe.getBoundingClientRect().left + currentClientX
    }

    return this.buttonSideDragState?.lastPageX ?? 0
  }

  private hideGreeting(): void {
    if (!GREETING_ENABLED) return
    this.sendMessageToGreetingIframe('hideGreeting', {})
    this.state.greetingDismissed = true
  }

  private dismissGreeting(): void {
    if (!GREETING_ENABLED) return
    this.hideGreeting()
  }

  private scheduleGreeting(): void {
    if (!GREETING_ENABLED) {
      if (this.state.greetingTimeout) {
        clearTimeout(this.state.greetingTimeout)
        this.state.greetingTimeout = null
      }
      return
    }
    if (this.state.greetingTimeout) {
      clearTimeout(this.state.greetingTimeout)
    }
    this.state.greetingTimeout = setTimeout(() => {
      this.showGreeting()
    }, GREETING_DELAY_MS)
  }

  private setupSystemThemeListener(): void {
    this.systemThemeListener = () => {
      this.notifyThemeChange()
    }

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this.systemThemeListener)
  }

  private setupSiteThemeObserver(): void {
    try {
      this.siteThemeObserver = new MutationObserver(() => {
        this.notifyThemeChange()
      })

      this.siteThemeObserver.observe(window.document.documentElement, {
        attributes: true,
        attributeFilter: ['style', 'class']
      })
    } catch (e) {
      this.logError('Failed to setup theme observer:', e)
    }
  }

  private notifyThemeChange(): void {
    if (this.state.settings && (this.state.settings.mode === 'system' || !this.state.settings.mode)) {
      const parentColorScheme = this.getParentColorScheme()
      const parentIsMobile = getParentIsMobileViewport()

      this.applyChatLoadingTheme()
      this.sendMessageToButtonIframe('applySettings', {settings: this.state.settings, parentColorScheme})
      this.sendMessageToChatIframe('applySettings', {settings: this.state.settings, parentColorScheme, parentIsMobile})
      this.sendMessageToGreetingIframe('applySettings', {settings: this.state.settings, parentColorScheme})
    }
  }

  private getParentColorScheme(): 'light' | 'dark' | undefined {
    try {
      return resolveParentColorScheme()
    } catch (e) {
      console.error('Failed to get parent color scheme:', e)
    }

    return undefined
  }

  private openChatContainer(iframeContainer: HTMLElement): void {
    this.applyChatLoadingTheme(iframeContainer)
    iframeContainer.classList.add(CHAT_OPEN_CLASS)

    if (this.state.isInitialized) {
      iframeContainer.classList.remove(CHAT_LOADING_CLASS)
    } else {
      iframeContainer.classList.add(CHAT_LOADING_CLASS)
    }
  }

  private closeChatContainer(iframeContainer: HTMLElement): void {
    iframeContainer.classList.remove(CHAT_OPEN_CLASS)
    iframeContainer.classList.remove(CHAT_LOADING_CLASS)
  }

  private handleSmallViewportUnavailable({showWarning}: {showWarning: boolean}): void {
    this.autoHiddenForSmallViewport = true

    const parentColorScheme = this.getParentColorScheme()
    const parentIsMobile = getParentIsMobileViewport()
    const iframeContainer = document.getElementById('alloy-webchat-widget-iframe-container')

    if (this.state.chatIframe) {
      this.sendMessageToChatIframe('close', {parentIsMobile})
    }

    this.state.isOpen = false
    this.disposeChatIframe()

    if (iframeContainer) {
      this.closeChatContainer(iframeContainer)
    }

    this.setParentScrollLock(false)
    this.disableMobileBackGuard()
    this.isVoiceCallActive = false

    this.sendMessageToButtonIframe('chatStateChange', {isOpen: false, parentColorScheme})
    this.sendMessageToButtonIframe('unreadCount', {count: this.unreadCount})
    this.sendMessageToButtonIframe('voiceCallStatus', {isActive: false})

    if (showWarning) {
      this.showSmallViewportWarning()
    }
  }

  private disposeChatIframe(): void {
    const chatIframe = this.state.chatIframe
    const chatIframeWrapper = chatIframe?.parentElement

    chatIframeWrapper?.remove()
    this.sourceRegistry?.clearChatSource()
    this.state.chatIframe = null
    this.state.isInitialized = false
    this.state.isInitializing = false
  }

  private reconcileSmallViewportState(parentIsMobile: boolean): boolean {
    if (this.canDisplayChat(parentIsMobile)) {
      if (this.autoHiddenForSmallViewport) {
        this.autoHiddenForSmallViewport = false
        this.open()
        return false
      }

      return true
    }

    if (this.state.isOpen || this.state.chatIframe) {
      this.handleSmallViewportUnavailable({showWarning: true})
    }

    return false
  }

  private canDisplayChat(parentIsMobile: boolean = getParentIsMobileViewport()): boolean {
    if (parentIsMobile) {
      const mobileViewportHeight = window.visualViewport?.height ?? window.innerHeight
      return mobileViewportHeight >= CHAT_WIDGET_MIN_USABLE_HEIGHT
    }

    const {maxWidth, maxHeight} = this.getAvailableDesktopChatSize()
    return maxWidth >= CHAT_WIDGET_MIN_WIDTH && maxHeight >= CHAT_WIDGET_MIN_USABLE_HEIGHT
  }

  private getAvailableDesktopChatSize(): {maxWidth: number; maxHeight: number} {
    const bounds = this.getResizeBoundsMetrics()

    return {
      maxWidth: Math.max(0, bounds.containerWidth - bounds.minLeft - bounds.minRight),
      maxHeight: Math.max(0, bounds.containerHeight - bounds.minTop - bounds.minBottom),
    }
  }

  private applyChatLoadingTheme(iframeContainer = document.getElementById('alloy-webchat-widget-iframe-container')): void {
    if (!iframeContainer) return

    const theme = this.getChatLoadingTheme()
    iframeContainer.style.setProperty('--alloy-webchat-loading-background', theme.background)
    iframeContainer.style.setProperty('--alloy-webchat-loading-foreground', theme.foreground)
    iframeContainer.style.setProperty('--alloy-webchat-loading-border', theme.border)
    iframeContainer.style.setProperty('--alloy-webchat-loading-track', theme.track)
    iframeContainer.style.setProperty('--alloy-webchat-loading-opacity', theme.opacity)
  }

  private getChatLoadingTheme(): {
    background: string
    foreground: string
    border: string
    track: string
    opacity: string
    } {
    const settings = this.asRecord(this.state.settings)
    const mode = this.asThemeMode(settings?.mode)
    const activeTheme = this.getActiveSettingsTheme(mode)
    const themes = this.asRecord(settings?.themes)
    const theme = this.asRecord(themes?.[activeTheme])
    const maximized = this.asRecord(theme?.maximized)

    const defaultBackground = activeTheme === 'dark' ? '#000000' : '#FFFFFF'
    const defaultForeground = activeTheme === 'dark' ? '#FFFFFF' : '#000000'
    const defaultBorder = activeTheme === 'dark' ? '#374151' : '#E5E7EB'
    const opacity = this.asOpacity(maximized?.opacity)

    return {
      background: this.asString(maximized?.chatBackground) || defaultBackground,
      foreground: this.asString(maximized?.primaryColor) || this.asString(maximized?.primaryTextColor) || defaultForeground,
      border: this.asString(maximized?.borderColor) || defaultBorder,
      track: this.asString(maximized?.borderColor) || defaultBorder,
      opacity,
    }
  }

  private getActiveSettingsTheme(mode: 'light' | 'dark' | 'system' | 'custom'): 'light' | 'dark' {
    if (mode === 'dark') return 'dark'
    if (mode === 'light' || mode === 'custom') return 'light'

    return this.getParentColorScheme() || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
  }

  private asThemeMode(value: unknown): 'light' | 'dark' | 'system' | 'custom' {
    return value === 'light' || value === 'dark' || value === 'system' || value === 'custom'
      ? value
      : 'system'
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : null
  }

  private asString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value : null
  }

  private asNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null
  }

  private asOpacity(value: unknown): string {
    const numericValue = typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value)
        : 100
    const clamped = Number.isFinite(numericValue) ? Math.min(Math.max(numericValue, 0), 100) : 100

    return String(clamped / 100)
  }

  private getStorageData(): Record<string, unknown> {
    try {
      const data = localStorage.getItem(STORAGE_KEY)
      return data ? JSON.parse(data) : {}
    } catch {
      return {}
    }
  }

  private setStorageData(updates: Record<string, unknown>): void {
    try {
      const current = this.getStorageData()
      const updated = { ...current, ...updates }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    } catch {
      // Ignore storage errors
    }
  }

  private logError(message: string, ...args: unknown[]): void {
    console.error(`[ChatWidget Error] ${message}`, ...args)
  }

  private getSmallViewportWarningMessage(): string {
    if (this.config.language === 'ru') {
      return 'Извините, окно слишком маленькое для отображения чат-виджета. Увеличьте окно браузера или откройте чат на другом устройстве.'
    }

    return 'Sorry, this window is too small to display the chat widget. Make the browser window larger or open chat on another device.'
  }

  private showSmallViewportWarning(): void {
    const warning = this.smallViewportWarningElement
    if (!warning) return

    warning.textContent = this.getSmallViewportWarningMessage()
    warning.classList.add('alloy-webchat-widget-small-viewport-warning-visible')

    if (this.smallViewportWarningTimer) {
      clearTimeout(this.smallViewportWarningTimer)
    }

    this.smallViewportWarningTimer = setTimeout(() => {
      warning.classList.remove('alloy-webchat-widget-small-viewport-warning-visible')
      this.smallViewportWarningTimer = null
    }, SMALL_VIEWPORT_WARNING_DURATION_MS)
  }

  private hideSmallViewportWarning(): void {
    const warning = this.smallViewportWarningElement
    warning?.classList.remove('alloy-webchat-widget-small-viewport-warning-visible')

    if (this.smallViewportWarningTimer) {
      clearTimeout(this.smallViewportWarningTimer)
      this.smallViewportWarningTimer = null
    }
  }

  private showError(message: string): void {
    const errorDiv = document.createElement('div')
    errorDiv.className = 'alloy-webchat-widget-error'
    errorDiv.textContent = message
    document.body.appendChild(errorDiv)

    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.parentNode.removeChild(errorDiv)
      }
    }, 5000)
  }

  private createChatIframeIfNeeded(): void {
    if (this.state.chatIframe) {
      return
    }

    const iframeContainer = document.getElementById('alloy-webchat-widget-iframe-container')
    if (!iframeContainer) {
      this.logError('Cannot create chat iframe: container not found')
      return
    }

    const chatIframeWrapper = document.createElement('div')
    chatIframeWrapper.className = 'alloy-webchat-widget-iframe-wrapper'

    this.state.chatIframe = document.createElement('iframe')
    this.state.chatIframe.className = 'alloy-webchat-widget-iframe'
    this.state.chatIframe.setAttribute('allow', 'microphone; camera; autoplay; display-capture; clipboard-write; clipboard-read')
    this.state.chatIframe.setAttribute('title', 'Chat Support')

    chatIframeWrapper.appendChild(this.state.chatIframe)

    iframeContainer.appendChild(chatIframeWrapper)

    requestAnimationFrame(() => {
      if (this.state.chatIframe) {
        this.state.chatIframe.src = `${this.getAssetUrl()}/widget/src/chat/index.html`
      }
    })

    const registerSource = () => {
      if (this.sourceRegistry && this.state.chatIframe?.contentWindow) {
        this.sourceRegistry.registerChatSource(this.state.chatIframe.contentWindow)
        return true
      }
      return false
    }

    if (!registerSource()) {
      const checkChatIframeInterval = setInterval(() => {
        if (registerSource()) {
          clearInterval(checkChatIframeInterval)
        }
      }, 100)

      setTimeout(() => {
        clearInterval(checkChatIframeInterval)
      }, 10000)
    }
  }

  private attachResizeHandles(iframeContainer: HTMLDivElement): void {
    if (getParentIsMobileViewport()) {
      return
    }

    const directions = this.getResizeHandleDirections()
    directions.forEach((direction) => {
      const handle = document.createElement('div')
      handle.className = `alloy-webchat-widget-resize-handle alloy-webchat-widget-resize-handle-${direction}`
      handle.dataset.direction = direction
      handle.addEventListener('pointerdown', this.handleResizeStart)
      iframeContainer.appendChild(handle)
    })
  }

  private updateResizeHandlesForSide(iframeContainer: HTMLDivElement): void {
    iframeContainer.querySelectorAll('.alloy-webchat-widget-resize-handle').forEach((handle) => {
      handle.remove()
    })
    this.attachResizeHandles(iframeContainer)
  }

  private getResizeHandleDirections(): string[] {
    return this.launcherSide === 'left' ? ['n', 'e', 'ne'] : ['n', 'w', 'nw']
  }

  private handleResizeStart = (event: PointerEvent): void => {
    if (!this.state.widgetContainer) return
    if (event.button !== 0) return

    const handle = event.currentTarget as HTMLElement | null
    const iframeContainer = handle?.parentElement as HTMLDivElement | null
    if (!handle || !iframeContainer) return

    const direction = handle.dataset.direction
    if (!direction) return

    const bounds = this.getResizeBoundsMetrics()
    const containerRect = iframeContainer.getBoundingClientRect()
    const computedStyle = window.getComputedStyle(iframeContainer)

    const minWidth = CHAT_WIDGET_MIN_WIDTH
    const minHeight = CHAT_WIDGET_MIN_HEIGHT
    const maxFromCssWidth = this.parsePixelValue(computedStyle.maxWidth)
    const maxFromCssHeight = this.parsePixelValue(computedStyle.maxHeight)
    const maxWidthFromBounds = Math.max(0, bounds.containerWidth - bounds.minLeft - bounds.minRight)
    const maxHeightFromBounds = Math.max(0, bounds.containerHeight - bounds.minTop - bounds.minBottom)
    const maxWidth = Math.max(0, Math.min(maxFromCssWidth ?? maxWidthFromBounds, maxWidthFromBounds))
    // Clamp the drag ceiling to the space actually available in the viewport.
    // Flooring at minHeight here would let a short viewport keep/request a chat
    // taller than the safe bounds; clampSize() already lowers the effective
    // minimum when maxHeight < minHeight, so 700 still wins whenever it fits.
    const maxHeight = Math.max(0, Math.min(maxFromCssHeight ?? maxHeightFromBounds, maxHeightFromBounds))
    const startSide = this.launcherSide
    const startLeft = this.parsePixelValue(computedStyle.left) ?? bounds.minLeft
    const startRight = this.parsePixelValue(computedStyle.right) ?? (bounds.containerWidth - startLeft - containerRect.width)
    const startBottom = this.parsePixelValue(computedStyle.bottom) ?? bounds.minBottom
    const startTop = bounds.containerHeight - startBottom - containerRect.height

    this.resizeState = {
      direction,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: containerRect.width,
      startHeight: containerRect.height,
      startSide,
      startRight,
      startBottom,
      startLeft,
      startTop,
      containerWidth: bounds.containerWidth,
      containerHeight: bounds.containerHeight,
      minLeft: bounds.minLeft,
      minTop: bounds.minTop,
      minRight: bounds.minRight,
      minBottom: bounds.minBottom,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight
    }

    iframeContainer.classList.add('alloy-webchat-widget-resizing')
    handle.setPointerCapture(event.pointerId)
    window.addEventListener('pointermove', this.handleResizeMove)
    window.addEventListener('pointerup', this.handleResizeEnd)
    event.preventDefault()
  }

  private handleResizeMove = (event: PointerEvent): void => {
    if (!this.resizeState) return
    const iframeContainer = document.getElementById('alloy-webchat-widget-iframe-container') as HTMLDivElement | null
    if (!iframeContainer) return

    const {
      direction,
      startX,
      startY,
      startWidth,
      startHeight,
      startSide,
      startRight,
      startBottom,
      startLeft,
      startTop,
      containerWidth,
      containerHeight,
      minLeft,
      minTop,
      minRight,
      minBottom,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight
    } = this.resizeState

    const dx = event.clientX - startX
    const dy = event.clientY - startY

    let width = startWidth
    let height = startHeight
    let right = startRight
    let left = startLeft
    let bottom = startBottom

    if (direction.includes('e')) {
      width = this.clampSize(startWidth + dx, minWidth, maxWidth)
      right = containerWidth - startLeft - width
    }

    if (direction.includes('w')) {
      width = this.clampSize(startWidth - dx, minWidth, maxWidth)
      left = containerWidth - startRight - width
    }

    if (direction.includes('s')) {
      height = this.clampSize(startHeight + dy, minHeight, maxHeight)
      bottom = containerHeight - startTop - height
    }

    if (direction.includes('n')) {
      height = this.clampSize(startHeight - dy, minHeight, maxHeight)
    }

    const clamped = this.clampToBounds({
      side: startSide,
      width,
      height,
      left,
      right,
      bottom,
      minLeft,
      minTop,
      minRight,
      minBottom,
      containerWidth,
      containerHeight,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight
    })

    iframeContainer.style.width = `${clamped.width}px`
    iframeContainer.style.height = `${clamped.height}px`
    if (startSide === 'left') {
      iframeContainer.style.left = `${clamped.left}px`
      iframeContainer.style.removeProperty('right')
    } else {
      iframeContainer.style.right = `${clamped.right}px`
      iframeContainer.style.removeProperty('left')
    }
    iframeContainer.style.bottom = `${clamped.bottom}px`
  }

  private handleResizeEnd = (event: PointerEvent): void => {
    const iframeContainer = document.getElementById('alloy-webchat-widget-iframe-container') as HTMLDivElement | null
    iframeContainer?.classList.remove('alloy-webchat-widget-resizing')

    if (event.target instanceof HTMLElement) {
      try {
        event.target.releasePointerCapture(event.pointerId)
      } catch {
        // Ignore pointer capture errors
      }
    }

    window.removeEventListener('pointermove', this.handleResizeMove)
    window.removeEventListener('pointerup', this.handleResizeEnd)
    this.resizeState = null
  }

  private setupResizeClampListener(): void {
    if (this.resizeClampListener) return

    this.resizeClampListener = () => {
      const iframeContainer = document.getElementById('alloy-webchat-widget-iframe-container') as HTMLDivElement | null
      if (!iframeContainer) return
      const parentIsMobile = getParentIsMobileViewport()

      if (!this.reconcileSmallViewportState(parentIsMobile)) {
        return
      }

      const computedStyle = window.getComputedStyle(iframeContainer)
      if (computedStyle.display === 'none') return

      this.sendMessageToChatIframe('parentViewportChange', { parentIsMobile })
      this.updateMobileBackGuard(parentIsMobile)

      if (parentIsMobile) {
        this.updateParentScrollLock()
        return
      }

      this.setParentScrollLock(false)

      const bounds = this.getResizeBoundsMetrics()
      const rect = iframeContainer.getBoundingClientRect()
      const left = this.parsePixelValue(computedStyle.left) ?? bounds.minLeft
      const right = this.parsePixelValue(computedStyle.right) ?? (bounds.containerWidth - left - rect.width)
      const bottom = this.parsePixelValue(computedStyle.bottom) ?? bounds.minBottom
      const maxWidthFromBounds = Math.max(0, bounds.containerWidth - bounds.minLeft - bounds.minRight)
	      const maxHeightFromBounds = Math.max(0, bounds.containerHeight - bounds.minTop - bounds.minBottom)

      const clamped = this.clampToBounds({
        side: this.launcherSide,
        width: rect.width,
        height: rect.height,
        left,
        right,
        bottom,
        minLeft: bounds.minLeft,
        minTop: bounds.minTop,
        minRight: bounds.minRight,
        minBottom: bounds.minBottom,
        containerWidth: bounds.containerWidth,
        containerHeight: bounds.containerHeight,
        minWidth: CHAT_WIDGET_MIN_WIDTH,
        minHeight: CHAT_WIDGET_MIN_HEIGHT,
        maxWidth: maxWidthFromBounds,
        maxHeight: maxHeightFromBounds
      })

      iframeContainer.style.width = `${clamped.width}px`
      iframeContainer.style.height = `${clamped.height}px`
      if (this.launcherSide === 'left') {
        iframeContainer.style.left = `${clamped.left}px`
        iframeContainer.style.removeProperty('right')
      } else {
        iframeContainer.style.right = `${clamped.right}px`
        iframeContainer.style.removeProperty('left')
      }
      iframeContainer.style.bottom = `${clamped.bottom}px`
    }

    window.addEventListener('resize', this.resizeClampListener)
    this.resizeClampListener()
  }

  private clampSize(value: number, min: number, max: number): number {
    const safeMax = Math.max(0, max)
    const safeMin = Math.min(min, safeMax)
    return Math.min(Math.max(value, safeMin), safeMax)
  }

  private clampPosition(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max)
  }

  private clampToBounds({
    side,
    width,
    height,
    left,
    right,
    bottom,
    minLeft,
    minTop,
    minRight,
    minBottom,
    containerWidth,
    containerHeight,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight
  }: {
    side: LauncherSide
    width: number
    height: number
    left: number
    right: number
    bottom: number
    minLeft: number
    minTop: number
    minRight: number
    minBottom: number
    containerWidth: number
    containerHeight: number
    minWidth: number
    minHeight: number
    maxWidth: number
    maxHeight: number
  }) {
    const safeMaxWidth = Math.max(0, maxWidth)
    const safeMaxHeight = Math.max(0, maxHeight)
    const safeMinWidth = Math.min(minWidth, safeMaxWidth)
    const safeMinHeight = Math.min(minHeight, safeMaxHeight)

    const clampedWidth = this.clampSize(width, safeMinWidth, safeMaxWidth)
    const clampedHeight = this.clampSize(height, safeMinHeight, safeMaxHeight)

    const maxLeft = Math.max(minLeft, containerWidth - minRight - clampedWidth)
    const maxRight = Math.max(minRight, containerWidth - minLeft - clampedWidth)
    const clampedLeft = side === 'left'
      ? this.clampPosition(left, minLeft, maxLeft)
      : containerWidth - this.clampPosition(right, minRight, maxRight) - clampedWidth
    const resolvedRight = side === 'left'
      ? containerWidth - clampedLeft - clampedWidth
      : this.clampPosition(right, minRight, maxRight)
    const maxBottom = Math.max(minBottom, containerHeight - minTop - clampedHeight)
    const clampedBottom = this.clampPosition(bottom, minBottom, maxBottom)

    return {
      width: clampedWidth,
      height: clampedHeight,
      left: clampedLeft,
      right: resolvedRight,
      bottom: clampedBottom
    }
  }

  private getResizeBoundsMetrics(): {
    containerWidth: number
    containerHeight: number
    minLeft: number
    minTop: number
    minRight: number
    minBottom: number
    } {
    const edgePadding = 25
    const bottomPadding = 110

    if (this.state.widgetContainer && this.state.widgetContainer.style.position === 'absolute') {
      const rect = this.state.widgetContainer.getBoundingClientRect()
      return {
        containerWidth: rect.width,
        containerHeight: rect.height,
        minLeft: edgePadding,
        minTop: edgePadding,
        minRight: edgePadding,
        minBottom: bottomPadding
      }
    }

    return {
      containerWidth: window.innerWidth,
      containerHeight: window.innerHeight,
      minLeft: edgePadding,
      minTop: edgePadding,
      minRight: edgePadding,
      minBottom: bottomPadding
    }
  }

  private parsePixelValue(value: string): number | null {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }
}
